import * as React from 'react';
import * as _ from 'underscore';

import { useInlineEditor, isReadonlyMode, InlineEditorLogic, InlineEditorLogic_EditMode } from '@tableau/vizportalclient-common-components';
import { TextAreaWidget } from '@tableau/widgets';
import { Messages, Formatters } from '../../../codegen/Localize';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { CollectionSpaceHeaderProps } from '../Collections/CollectionSpaceHeader';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { CommonStyles } from '../../../common/react/CommonStyles';

export interface InlineEditableDescriptionOwnProps {
  readonly maxCharacterCount: number;
  readonly updateDescription: (description: string) => Promise<void>;
  readonly sanitizer: ng.sanitize.ISanitizeService;
}

export type InlineEditableDescriptionProps = InlineEditableDescriptionOwnProps & CollectionSpaceHeaderProps;

export function InlineEditableDescription(props: InlineEditableDescriptionProps): JSX.Element {
  const [description, setDescription] = React.useState(props.item.description);

  const isValidDescription = React.useCallback((description: string): boolean => {
    if (description) {
      return description.length >= 0 && description.length <= props.maxCharacterCount;
    }
    return true;
  },[]);

  const onSave = async (description: string): Promise<void> => {
    try {
      await props.updateDescription(description);
      logic.inEditMode && logic.setEditorStateWithinEditMode(description);
      setDescription(description);
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.unexpectedError()
      });
      throw e;
    }
  };

  const logic: InlineEditorLogic<string> = useInlineEditor<string>({
    editButtonLabel: Messages.editDescription2(),
    editorStateWhenEnteringEditMode: description,
    canEdit: props.isAdminOrOwner,
    onSave: onSave,
    canSave: isValidDescription,
    exitEditModeOnFailedSave: true,
  });

  const showDescription = props.isAdminOrOwner || description;

  if (isReadonlyMode(logic)) {
    return <>
      { showDescription &&
        <div
          data-tb-test-id={'editable-description-readonly-mode'}
          className={props.isAdminOrOwner ? SpaceHeaderStyles.HeaderEditableInlineDescriptionReadonly : SpaceHeaderStyles.HeaderInlineDescriptionReadonly}
          onClick={() => { props.isAdminOrOwner && logic.enterEditMode(); }}
        >
          { description && <span>{TextileHelper.getPlainTextFromTextile(description, props.sanitizer)}</span>}
          { !description && <span style={{color: CommonStyles.Colors.Grey112}}>{Messages.collectionDescriptionPrompt()}</span>}
          <span style={{height: '100%'}}>{logic.editButton}</span>
        </div>
      }
    </>;
  } else {
    return <InlineEditableDescriptionEditMode logic={logic} maxCharacterCount={props.maxCharacterCount}/>;
  }
}

export interface InlineEditableDescriptionEditModeProps {
  readonly logic: InlineEditorLogic_EditMode<string>;
  readonly maxCharacterCount: number;
}

export function InlineEditableDescriptionEditMode(props: InlineEditableDescriptionEditModeProps): JSX.Element {
  const getMessageForDescriptionTextArea = React.useCallback((value: string): string => {
    const remainingChars = value ? props.maxCharacterCount - value.length : props.maxCharacterCount;
    return Messages.remainingCharacters({
      COUNT: remainingChars,
      COUNT_LOC: Formatters.formatCount(remainingChars)
    });
  }, [props.maxCharacterCount]);

  const [message, setMessage] = React.useState(getMessageForDescriptionTextArea(props.logic.editorState));

  const onDescriptionChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    props.logic.setEditorStateWithinEditMode(value);
    setMessage(getMessageForDescriptionTextArea(value));
  }, [props.logic.setEditorStateWithinEditMode]);

  return <span data-tb-test-id={'editable-description-edit-mode'} style={{ display: 'block', maxWidth: 700 }}>
    <TextAreaWidget
      testId='collection-description-edit'
      label={undefined}
      maxLength={props.maxCharacterCount}
      onChange={onDescriptionChange}
      value={props.logic.editorState}
      message={message}
      className={SpaceHeaderStyles.HeaderInlineDescriptionTextArea}
    />
    <div className={SpaceHeaderStyles.ConfirmCancelButtons}>
      {props.logic.cancelButton} {props.logic.saveButton}
    </div>
  </span>;
}
